/**
 * Engine i18n dùng chung Viewer + Console (design 09a). JS/TS THUẦN, KHÔNG phụ thuộc Svelte —
 * vì `@wood-viewer/shared` được tiêu thụ qua node_modules (symlink), nơi vite-plugin-svelte
 * KHÔNG biên dịch `.svelte.ts`/rune. Do đó:
 *   - shared = hàm thuần (translate/plural/format/detect) — không reactive.
 *   - reactivity (`$state`) đặt ở LỚP APP: mỗi module (Viewer 09b / Console 09c) có 1 wrapper
 *     `.svelte.ts` giữ `locale` bằng rune; gọi `t()` đọc rune trong template → tự cập nhật.
 *
 * Quy ước (kế thừa plugin i18n.js): vi = NGUỒN + fallback; `${k}` interpolation (thiếu giữ `${k}`);
 * thiếu key → fallback vi → trả chính key (lộ chỗ sót); giữ nguyên brand/kỹ thuật (WoodViewer,
 * BAZIS, mm, QR, Author…) — người dịch tự tuân thủ, engine không enforce.
 */

export const LOCALES = ['vi', 'en', 'ru', 'zh', 'th'] as const;
export type Locale = (typeof LOCALES)[number];

/** Tên ngôn ngữ hiển thị trong selector (endonym — giữ nguyên qua mọi locale). */
export const LANG_NAMES: Record<Locale, string> = {
  vi: 'Tiếng Việt',
  en: 'English',
  ru: 'Русский',
  zh: '中文',
  th: 'ไทย',
};

/** Từ điển 1 locale: key phẳng → chuỗi (có thể chứa `${param}`). */
export type Dict = Record<string, string>;
/** Từ điển đủ 5 locale. `vi` bắt buộc (nguồn + fallback). */
export type Dicts = Record<Locale, Dict>;

export type Params = Record<string, string | number>;

/** Thay `${k}` bằng params[k]; thiếu param → giữ `${k}` để lộ chỗ sai. */
function interpolate(str: string, params?: Params): string {
  if (!params) return str;
  return str.replace(/\$\{(\w+)\}/g, (_, k: string) =>
    params[k] != null ? String(params[k]) : '${' + k + '}',
  );
}

/** Dịch 1 key. Fallback: locale → vi → trả chính key. */
export function translate(dicts: Dicts, locale: Locale, key: string, params?: Params): string {
  const s = dicts[locale]?.[key] ?? dicts.vi[key];
  if (s == null) return key;
  return interpolate(s, params);
}

/**
 * Số nhiều theo `Intl.PluralRules(locale)`. Key có biến thể `key.one/.few/.many/.other`.
 * `n` tự thêm vào params. RU: 1→one, 2–4→few, 5+→many; vi/zh/th→other; en: 1→one, khác→other.
 */
export function plural(
  dicts: Dicts,
  locale: Locale,
  key: string,
  n: number,
  params?: Params,
): string {
  const cat = new Intl.PluralRules(locale).select(n);
  const p: Params = { ...params, n };
  const dl = dicts[locale] ?? {};
  const dv = dicts.vi;
  const s = dl[`${key}.${cat}`] ?? dl[`${key}.other`] ?? dv[`${key}.${cat}`] ?? dv[`${key}.other`];
  if (s == null) return `${key}.${cat}`;
  return interpolate(s, p);
}

/** Định dạng ngày theo locale. */
export function fmtDate(
  locale: Locale,
  d: string | number | Date,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const date = d instanceof Date ? d : new Date(d);
  return new Intl.DateTimeFormat(locale, opts).format(date);
}

/** Định dạng số theo locale. */
export function fmtNum(locale: Locale, n: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(locale, opts).format(n);
}

/**
 * Chọn locale khớp LOCALES từ danh sách ưu tiên (vd: ?lang, đã lưu, ...navigator.languages).
 * Khớp theo phần gốc ('en-US' → 'en'). Không khớp → 'vi'.
 */
export function detectLocale(...candidates: (string | null | undefined)[]): Locale {
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split('-')[0];
    const hit = LOCALES.find((l) => l === base);
    if (hit) return hit;
  }
  return 'vi';
}

/**
 * Khóa localStorage NGÔN NGỮ — DÙNG CHUNG Console + Viewer.
 * Hai app cùng origin (woodviewer.com / dev qua proxy :5173) → chia sẻ localStorage,
 * nên một lựa chọn ngôn ngữ áp dụng cho cả hai (đồng bộ). Đổi key sẽ phá đồng bộ.
 */
export const LANG_STORAGE_KEY = 'wv_lang';

/** Trả Locale nếu chuỗi khớp (theo gốc 'en-US'→'en'); ngược lại null (KHÔNG fallback vi). */
export function asLocale(c: string | null | undefined): Locale | null {
  if (!c) return null;
  const base = c.toLowerCase().split('-')[0];
  return (LOCALES as readonly string[]).includes(base) ? (base as Locale) : null;
}

/**
 * Mặc định TẠM (đồng bộ) khi user CHƯA cài đặt — chỉ vi/en để khớp quy tắc theo IP.
 * Đoán nhanh theo navigator (vi → vi, còn lại → en) nhằm giảm "nháy" trước khi có kết quả IP.
 */
export function provisionalLocale(): Locale {
  const navs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  return navs.some((c) => c?.toLowerCase().startsWith('vi')) ? 'vi' : 'en';
}

const GEO_CACHE_KEY = 'wv_geo_country'; // cache mã quốc gia trong phiên để khỏi gọi lại

/**
 * Lấy mã quốc gia 2 chữ (ISO 3166-1 alpha-2) theo IP — dùng chung cho mọi logic phân vùng.
 * Nguồn chính: Cloudflare `/cdn-cgi/trace` (same-origin, prod woodviewer.com) → `loc=VN`.
 * Fallback: `api.country.is` (free, không API key) — cho trường hợp không qua Cloudflare
 * (vd. khách bị chặn → truy cập viewer.woodsoft.vn trực tiếp đến VPS).
 * Cache vào sessionStorage để chỉ gọi 1 lần/phiên. Trả null nếu không xác định được.
 */
export async function getGeoCountry(timeoutMs = 2500): Promise<string | null> {
  if (typeof fetch === 'undefined') return null;

  // 1. Cache hit → trả ngay
  try {
    const cached = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(GEO_CACHE_KEY) : null;
    if (cached) return cached;
  } catch {
    /* ignore */
  }

  // 2. Thử Cloudflare /cdn-cgi/trace (same-origin, nhanh)
  const country = await _fetchCloudflareCountry(timeoutMs) ?? await _fetchFallbackCountry(timeoutMs);

  // 3. Cache kết quả
  if (country) {
    try {
      sessionStorage.setItem(GEO_CACHE_KEY, country);
    } catch {
      /* ignore */
    }
  }
  return country;
}

/** Cloudflare trace → dòng `loc=VN`. */
async function _fetchCloudflareCountry(timeoutMs: number): Promise<string | null> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch('/cdn-cgi/trace', { signal: ctrl.signal, cache: 'no-store' });
    clearTimeout(timer);
    if (!res.ok) return null;
    const text = await res.text();
    return /^loc=([A-Z]{2})/m.exec(text)?.[1] ?? null;
  } catch {
    return null;
  }
}

/** Fallback: api.country.is → { country: "VN" }. */
async function _fetchFallbackCountry(timeoutMs: number): Promise<string | null> {
  try {
    const res = await fetch('https://api.country.is/', { signal: AbortSignal.timeout(timeoutMs) });
    if (!res.ok) return null;
    const { country } = (await res.json()) as { country?: string };
    return country ?? null;
  } catch {
    return null;
  }
}

/**
 * Locale MẶC ĐỊNH theo IP: IP ở Việt Nam → vi, còn lại → en. Trả null nếu không xác định được.
 * Tận dụng `getGeoCountry()` — cùng 1 lần gọi Cloudflare, cùng cache.
 * KHÔNG ghi localStorage — đây là "mặc định", không phải "cài đặt".
 */
export async function geoDefaultLocale(timeoutMs = 2500): Promise<Locale | null> {
  const country = await getGeoCountry(timeoutMs);
  if (!country) return null;
  return country === 'VN' ? 'vi' : 'en';
}

